import React from "react";

import CloseIcon from "@uui/XClose";

import type { Theme } from "@mui/material";
import Collapse from "@mui/material/Collapse";
import MuiDialog from "@mui/material/Dialog";
import styled from "@mui/material/styles/styled";

import { lightOrDark } from "@app/embedded/theme/utils";

import Button, { ButtonProps } from "@rui/Button";
import Dropdown from "@rui/Dropdown";
import IconButton, { IconButtonProps } from "@rui/IconButton";
import Stack, { Row, StackProps } from "@rui/Stack";

export const Dialog = styled(MuiDialog)(({ fullScreen, theme }) => ({
  borderColor: theme.palette.shades.neutral[50],
  ...(fullScreen && {
    borderTop: "1px solid",
    borderBottom: "1px solid",
  }),
}));

export const CloseButton = styled<React.FC<IconButtonProps>>((props) => (
  <IconButton {...props}>
    <CloseIcon />
  </IconButton>
))(({ theme }) => ({
  border: "1px solid",
  borderColor: theme.palette.shades.neutral[100],
  borderRadius: "8px",
  height: "32px",
  width: "32px",
}));

export const Container = styled(Stack)(({ theme }) => ({
  flexShrink: 0,
  width: "100%",
  minHeight: "100%",
  justifyContent: "space-between",
  backgroundColor: "#FFF",
  [theme.breakpoints.up("sm")]: {
    width: "600px",
    maxWidth: "100%",
    height: "auto",
  },
}));

export const ContentContainer = styled(Stack)(({ theme }) => ({
  flexGrow: 1,
  justifyContent: "stretch",
  padding: theme.spacing(0, 2),
  [theme.breakpoints.up("sm")]: {
    padding: theme.spacing(0, 3),
  },
}));

export const Header = styled(Row)(({ theme }) => ({
  justifyContent: "space-between",
  padding: theme.spacing(2),
  borderBottom: "1px solid",
  borderColor: theme.palette.shades.neutral[100],
  [theme.breakpoints.up("sm")]: {
    padding: theme.spacing(3),
  },
}));

export const SharingButton = styled((props: ButtonProps) => (
  <Button {...props} />
))({
  minWidth: "100px",
});

export const DisclaimerContainer = styled(Stack)(({ theme }) => ({
  padding: theme.spacing(1),
  borderRadius: "4px",
  backgroundColor: lightOrDark(
    theme.palette.shades.ghost[300],
    theme.palette.shades.primary[800]
  )(theme),
  color: lightOrDark(
    theme.palette.shades.neutral[700],
    theme.palette.shades.neutral[50]
  )(theme),
}));

export const AccessListCollapse = styled(Collapse)(({ in: inProp, theme }) => ({
  height: "100%",

  [theme.breakpoints.down("xxs")]: {
    flexGrow: 1,
    opacity: inProp ? 1 : 0,
    transition: "opacity 0.4s ease",
  },
  [theme.breakpoints.up("sm")]: {
    height: "auto",
  },
  "& .MuiCollapse-wrapper": {
    height: "100%",
    [theme.breakpoints.up("sm")]: {
      height: "auto",
    },
  },
}));

export const AccessListWrapper = styled(Stack)(({ theme }) => ({
  [theme.breakpoints.down("xxs")]: {
    flexGrow: 1,
    height: "100%",
  },
  marginBottom: theme.spacing(1),
}));

export const Footer = styled(Stack)(({ theme }) => ({
  position: "sticky",
  bottom: 0,
  padding: theme.spacing(2, 2),
  backgroundColor: "#FFF",
  boxShadow: "0px -4px 4px 0px #0000000A",

  [theme.breakpoints.up("sm")]: {
    borderTop: "1px solid",
    borderColor: theme.palette.shades.neutral[100],
    padding: theme.spacing(2, 3),
    boxShadow: "none",
  },
}));

type EntityListProps = StackProps & {
  maxHeight?: React.CSSProperties["maxHeight"];
};

export const EntityList = styled<React.FC<EntityListProps>>(
  ({ maxHeight: _mh, ...props }) => <Stack {...props} />
)(({ maxHeight = "180px", theme }) => ({
  height: maxHeight,
  maxWidth: "552px",
  border: `1px solid ${theme.palette.shades.neutral[100]}`,
  borderRadius: "6px",
  padding: theme.spacing(1),
  flex: 1,
  "::-webkit-scrollbar": {
    display: "none",
  },

  [theme.breakpoints.up("sm")]: {
    padding: theme.spacing(1.5),
    height: maxHeight,
    maxHeight,
    overflowY: "auto",
  },
}));

export const AccessSelectorDropdown = styled(Dropdown)(({ theme }) => ({
  minHeight: "0px",
  height: "100%",
  width: "100%",
  "& .MuiSelect-select": {
    display: "flex",
    height: "100% !important",
    fontWeight: 500,
  },
  "&:hover": {
    backgroundColor: lightOrDark(
      theme.palette.shades.ghost[300],
      theme.palette.transparent.white[4]
    )(theme as Theme),
    "& .MuiSelect-select": {
      color: lightOrDark(
        theme.palette.shades.primary[500],
        theme.palette.shades.primary[300]
      )(theme as Theme),
    },
  },
  "& .MuiOutlinedInput-input": {
    padding: theme.spacing(0.5, 0, 0.5, 1),
  },
  "& .MuiOutlinedInput-notchedOutline": {
    borderRadius: "8px",
    borderColor: theme.palette.shades.neutral[150],
  },
}));
